import React, { Fragment, useEffect, useState } from "react";
import { CardBody, Col, Row, Table, CardHeader } from "reactstrap";
// import { Link } from "react-router-dom";

import {
  Column,
  Table as ReactTable,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";

import { rankItem } from "@tanstack/match-sorter-utils";

import {
  ProductsGlobalFilter,
  CustomersGlobalFilter,
  ContactsGlobalFilter,
  CompaniesGlobalFilter,
  LeadsGlobalFilter,
  CryptoOrdersGlobalFilter,
  InvoiceListGlobalSearch,
  TicketsListGlobalFilter,
  NFTRankingGlobalFilter,
  TaskListGlobalFilter,
  PresetQuoteFilter,
  ClientDetailFilter,
  QuoteFilter,
} from "../../Components/Common/GlobalSearchFilter";
import Icon from "Components/Icons/Icon";
import { getPageNumbers } from "utils/utils";
import CustomButton from "Components/Custom/CustomButton";

// Column Filter
const Filter = ({
  column,
}: {
  column: Column<any, unknown>;
  table: ReactTable<any>;
}) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? "") as string}
        onChange={(value) => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + "list"}
      />
      <div className="h-1" />
    </>
  );
};

// Global Filter
const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input
      {...props}
      value={value}
      id="search-bar-0"
      className="form-control search"
      onChange={(e) => setValue(e.target.value)}
    />
  );
};

interface TableContainerProps {
  columns?: any;
  data?: any;
  isGlobalFilter?: any;
  isProductsFilter?: any;
  isCustomerFilter?: any;
  isContactsFilter?: any;
  isCompaniesFilter?: any;
  isLeadsFilter?: any;
  isCryptoOrdersFilter?: any;
  isInvoiceListFilter?: any;
  isTicketsListFilter?: any;
  isNFTRankingFilter?: any;
  isTaskListFilter?: any;
  isListFilter?: boolean;
  handleTaskClick?: any;
  customPageSize?: any;
  tableClass?: any;
  theadClass?: any;
  trClass?: any;
  thClass?: any;
  divClass?: any;
  searchClass?: any;
  SearchPlaceholder?: any;
  handleLeadClick?: any;
  handleCompanyClick?: any;
  handleContactClick?: any;
  handleTicketClick?: any;
  isQuoteFilter?: boolean;
  isPresetsFilter?: boolean;
  productCatyegory?: { label: string; value: number }[];
  selectedCategory?: number;
  onSearch?: (value: string) => void;
  onPageChange?: (page: number) => void;
  searchAlign?: "left" | "right";
  leftContent?: React.ReactNode;
  isClientDetails?: boolean;
  onClientDetailFilterClick?: () => void;
  onAddItemClick?: () => void;
  onItemFilterClick?: () => void;
  currentPage?: number;
  totalPages?: number;
  rightContent?: React.ReactNode;
  onClickNewSection?: () => void;
  onListFilter?: () => void;
  onNewQuoteClick?: () => void;
  handleSelectChange?: (e: number) => void;
  showPagination?: boolean;
  isCategorySearching?: boolean;
  handleCatergorySearch?: (search: string) => void;
  totalItems?: number;
  isInitialLoading?: boolean;
  isPaginationLoading?: boolean;
  fromPage?: number;
  toPage?: number;
  onRowClick?: (row: any) => void;
  colorName?: string;
  tableId?: string;
  hideSearch?: boolean;
  isPaymentDetails?: boolean;
  onCreatePaymentRecordClick?: () => void;
  isShowRecordPaymentButton?: boolean;
}

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  isProductsFilter,
  isCustomerFilter,
  isContactsFilter,
  isCompaniesFilter,
  isLeadsFilter,
  isCryptoOrdersFilter,
  isInvoiceListFilter,
  isTicketsListFilter,
  isNFTRankingFilter,
  isTaskListFilter,
  isListFilter,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  SearchPlaceholder,
  isQuoteFilter,
  isPresetsFilter,
  onSearch,
  onPageChange,
  searchAlign,
  isClientDetails,
  onClientDetailFilterClick,
  onAddItemClick,
  onItemFilterClick,
  currentPage = 1,
  totalPages = 1,
  rightContent,
  leftContent,
  onListFilter,
  onNewQuoteClick,
  showPagination = true,
  totalItems = 0,
  isInitialLoading = false,
  isPaginationLoading = false,
  fromPage,
  toPage,
  searchClass,
  onRowClick,
  colorName,
  tableId,
  hideSearch = false,
  isPaymentDetails,
  onCreatePaymentRecordClick,
  isShowRecordPaymentButton = false,
}: TableContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  // Trigger onSearch callback when globalFilter changes
  useEffect(() => {
    if (onSearch) {
      onSearch(globalFilter);
    }
  }, [globalFilter, onSearch]);

  useEffect(() => {
    // If current page has no rows but previous pages exist
    if (
      !isInitialLoading &&
      !isPaginationLoading &&
      data?.length === 0 &&
      currentPage > 1
    ) {
      onPageChange?.(currentPage - 1);
    }
  }, [data, currentPage, isInitialLoading, isPaginationLoading, onPageChange]);

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({
      itemRank,
    });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { getHeaderGroups, getRowModel, setPageSize } = table;

  useEffect(() => {
    Number(customPageSize) && setPageSize(Number(customPageSize));
  }, [customPageSize, setPageSize]);

  return (
    <Fragment>
      {isGlobalFilter && (
        <Row className="mb-3">
          {isClientDetails && (
            <Col lg={12}>
              <CardHeader className="mt-2 px-0 border-0">
                <div className="d-flex align-items-center justify-content-between w-full ">
                  <h5 className="flex-shrink-0">Recent Activity</h5>

                  <button
                    className="btn btn-primary ms-auto br-8"
                    onClick={onNewQuoteClick}
                  >
                    <i className="ri-add-fill me-1 align-bottom"></i>
                    New Quote
                  </button>
                </div>
                <div className="divider mt-3 mb-n2 mx-n4" />
              </CardHeader>
            </Col>
          )}

          <CardBody className="">
            <form>
              <Row className="align-items-center row-wrapper">
                {/* LEFT SIDE CONTENT */}
                {leftContent && (
                  <Col
                    sm="auto"
                    className={searchAlign === "right" ? "order-1" : ""}
                  >
                    {leftContent}
                  </Col>
                )}

                {/* SEARCH BAR AREA */}
                {!hideSearch && (
                  <Col
                    sm={`${searchAlign && "auto"}`}
                    className={
                      searchClass
                        ? `${searchClass}`
                        : isQuoteFilter
                        ? "d-flex align-items-center justify-content-center"
                        : searchAlign === "right"
                        ? "ms-auto order-2  d-flex align-items-center "
                        : "d-flex align-items-center justify-content-between"
                    }
                  >
                    <div className="d-flex w-100">
                      <div className="search-container">
                        <Icon name="Search" className="search-icon" />
                        <DebouncedInput
                          value={globalFilter ?? ""}
                          onChange={(value) =>
                            setGlobalFilter(String(value)?.trim())
                          }
                          placeholder={SearchPlaceholder}
                          className="bg-danger"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* {isQuoteFilter && (
                    <div className="position-relative d-flex w-100 ms-3">
                      <Select
                        name="category"
                        id="category-field"
                        className="mx-2"
                        placeholder="By Category"
                        onChange={(option: any) =>
                          handleSelectChange?.(option?.value ?? null)
                        }
                        onInputChange={(e: string) => {
                          handleCatergorySearch?.(e);
                        }}
                        isSearchable={true}
                        isClearable={true} // Changed to true for consistency
                        isInitialLoading={isCategorySearching}
                        value={
                          selectedCategory
                            ? productCatyegory?.find(
                                (cat: any) =>
                                  (typeof cat === "string"
                                    ? cat
                                    : cat.value) === selectedCategory
                              ) || {
                                value: selectedCategory,
                                label: selectedCategory,
                              }
                            : null
                        }
                        options={productCatyegory?.map((cat: any) =>
                          typeof cat === "string"
                            ? { value: cat, label: cat }
                            : cat
                        )}
                        menuPlacement="auto"
                        styles={{
                          container: (base: any) => ({
                            ...base,
                            minWidth: "200px",
                            width: "200px",
                          }),
                          control: (base: any) => ({
                            ...base,
                            minHeight: "38px",
                            height: "38px",
                            minWidth: "200px",
                            width: "200px",
                            border: "1px solid #ced4da",
                            borderRadius: "8px",
                            boxShadow: "none",
                            "&:hover": {
                              borderColor: "#ced4da",
                              boxShadow: "none",
                            },
                          }),
                          valueContainer: (base: any) => ({
                            ...base,
                            height: "36px",
                            padding: "0 8px",
                            display: "flex",
                            alignItems: "center",
                          }),
                          input: (base: any) => ({
                            ...base,
                            margin: "0",
                            padding: "0",
                            height: "36px",
                          }),
                          indicatorsContainer: (base: any) => ({
                            ...base,
                            height: "36px",
                          }),
                          indicatorSeparator: () => ({
                            display: "none",
                          }),
                          dropdownIndicator: (base: any) => ({
                            ...base,
                            padding: "8px",
                          }),
                          clearIndicator: (base: any) => ({
                            ...base,
                            padding: "8px",
                          }),
                          singleValue: (base: any) => ({
                            ...base,
                            margin: "0",
                          }),
                          placeholder: (base: any) => ({
                            ...base,
                            margin: "0",
                          }),
                          menu: (base: any) => ({
                            ...base,
                            zIndex: 9999,
                            minWidth: "200px",
                            width: "200px",
                            borderRadius: "8px",
                          }),
                        }}
                      />
                    </div>
                  )} */}

                    {isListFilter && (
                      <CustomButton
                        onClick={onListFilter}
                        text="Filter"
                        className="bg-blue br-7"
                        leftIcon={<Icon name="Equalizer" className="ms-1" />}
                      />
                    )}
                  </Col>
                )}

                {isQuoteFilter && (
                  <Col sm="auto" className="ms-auto order-2">
                    <QuoteFilter
                      onAddItemClick={onAddItemClick}
                      onItemFilterClick={onItemFilterClick}
                    />
                  </Col>
                )}
                {isClientDetails && (
                  <ClientDetailFilter onClick={onClientDetailFilterClick} />
                )}
                {isProductsFilter && <ProductsGlobalFilter />}
                {isPresetsFilter && (
                  <PresetQuoteFilter onAddItemClick={onAddItemClick} />
                )}
                {isCustomerFilter && <CustomersGlobalFilter />}

                {rightContent && (
                  <Col sm="auto" className="ms-auto order-2">
                    {rightContent}
                  </Col>
                )}
                {isContactsFilter && <ContactsGlobalFilter />}
                {isCompaniesFilter && <CompaniesGlobalFilter />}
                {isLeadsFilter && <LeadsGlobalFilter />}
                {isCryptoOrdersFilter && <CryptoOrdersGlobalFilter />}
                {isInvoiceListFilter && <InvoiceListGlobalSearch />}
                {isTicketsListFilter && <TicketsListGlobalFilter />}
                {isNFTRankingFilter && <NFTRankingGlobalFilter />}
                {isTaskListFilter && <TaskListGlobalFilter />}
              </Row>
            </form>
          </CardBody>
        </Row>
      )}
      {isPaymentDetails && (
        <Col lg={12}>
          <CardHeader className="my-2 px-0">
            <div className="d-flex align-items-center justify-content-between w-full ">
              <h5 className="flex-shrink-0">Payments</h5>

              {isShowRecordPaymentButton && (
                <button
                  className="btn btn-primary ms-auto br-8"
                  onClick={onCreatePaymentRecordClick}
                >
                  <i className="ri-add-fill me-1 align-bottom"></i>
                  Record Payment
                </button>
              )}
            </div>
            <div className="divider mt-3 mb-2 mx-n4" />
          </CardHeader>
        </Col>
      )}

      <div className={divClass}>
        <Table
          hover={getRowModel().rows.length > 0 ? true : false}
          className={tableClass}
          id={tableId}
        >
          <thead className={theadClass}>
            {getHeaderGroups().map((headerGroup: any) => (
              <tr className={trClass} key={headerGroup.id}>
                {headerGroup.headers.map((header: any) => (
                  <th
                    key={header.id}
                    className={thClass}
                    style={{
                      cursor: header.column.getCanSort()
                        ? "pointer"
                        : "default",
                      color: colorName,
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder ? null : (
                      <React.Fragment>
                        {/* Flex container to push arrow to the end */}
                        <div className="d-flex align-items-center justify-content-between w-100 gap-3">
                          {/* Column Header Text */}
                          <span>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </span>

                          {/* Sorting Dual-Arrow SVG at the end */}
                          {header.column.getCanSort() && (
                            <span
                              className="d-inline-block"
                              style={{ verticalAlign: "middle" }}
                            >
                              <svg
                                width="6"
                                height="10"
                                viewBox="0 0 6 10"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                {/* Top Arrow (ASC) */}
                                <path
                                  d="M3 0L6 3H0L3 0Z"
                                  fill={
                                    header.column.getIsSorted() === "asc"
                                      ? "#2C3E50"
                                      : "#89909D"
                                  }
                                />
                                {/* Bottom Arrow (DESC) */}
                                <path
                                  d="M3 10L0 7L6 7L3 10Z"
                                  fill={
                                    header.column.getIsSorted() === "desc"
                                      ? "#2C3E50"
                                      : "#89909D"
                                  }
                                />
                              </svg>
                            </span>
                          )}
                        </div>

                        {/* Column Filter (if enabled) */}
                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} table={table} />
                          </div>
                        ) : null}
                      </React.Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {isInitialLoading ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  <div className="spinner-border text-primary" />
                </td>
              </tr>
            ) : getRowModel().rows.length > 0 ? (
              <>
                {getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        onClick={() => onRowClick && onRowClick(row.original)}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                {isPaginationLoading && (
                  <tr>
                    <td colSpan={columns.length} className="text-center py-2">
                      <div className="spinner-border text-primary" />
                    </td>
                  </tr>
                )}
              </>
            ) : (
              <tr className="px-0 mx-0">
                <td colSpan={columns.length} className="text-center pb-0 px-0">
                  <hr
                    className="my-3 text-grey89"
                    // style={{ marginLeft: -15, marginRight: -15 }}
                  />
                  <p className="m-0 text-black2D fs-14 fw-normal font-primary">
                    No results found
                  </p>
                  <hr
                    className="my-3 mb-0 text-grey89 "
                    // style={{ marginLeft: -25, marginRight: -25 }}
                  />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {showPagination && !isInitialLoading && (
        <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
          <div className="col-sm">
            <div className="col-sm">
              {totalItems > 0 && (
                <div className="text-greyA6 font-primary fs-12 fw-normal">
                  Showing <span className="fw-semibold">{fromPage}</span> to{" "}
                  <span className="fw-semibold">{toPage}</span> of{" "}
                  <span className="fw-semibold">{totalItems}</span> items
                </div>
              )}
            </div>
          </div>
          <div className="col-sm-auto">
            <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
              {/* Previous */}
              {/* <li
                className={
                  currentPage === 1 ? "page-item disabled" : "page-item"
                }
              >
                <Link
                  to="#"
                  className="page-link fs-14 text-grey89 fw-normal br-4"
                  onClick={(e) => {
                    e.preventDefault();
                    (currentPage || 1) > 1 &&
                      onPageChange?.((currentPage || 1) - 1);
                  }}
                >
                  Previous
                </Link>
              </li> */}
              <li
                className={
                  (currentPage || 1) === 1 ? "page-item disabled" : "page-item"
                }
              >
                <button
                  className="page-link"
                  onClick={(e) => {
                    e.preventDefault();
                    (currentPage || 1) > 1 &&
                      onPageChange?.((currentPage || 1) - 1);
                  }}
                  disabled={isPaginationLoading}
                >
                  Previous
                </button>
              </li>

              {/* Dynamic Pages */}
              {getPageNumbers(currentPage || 1, totalPages || 1).map(
                (p, index) => (
                  <li
                    key={index}
                    className={`page-item ${
                      p === currentPage ? "active" : ""
                    } ${p === "..." ? "disabled" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        if (p !== "..." && onPageChange) {
                          onPageChange(Number(p));
                        }
                      }}
                      disabled={isPaginationLoading}
                    >
                      {p}
                    </button>
                  </li>
                ),
              )}

              {/* Next */}
              <li
                className={
                  currentPage === totalPages
                    ? "page-item disabled"
                    : "page-item"
                }
              >
                <button
                  className="page-link"
                  onClick={(e) => {
                    e.preventDefault();
                    (currentPage || 1) < (totalPages || 1) &&
                      onPageChange?.((currentPage || 1) + 1);
                  }}
                  disabled={isPaginationLoading}
                >
                  Next
                </button>
              </li>
            </ul>
          </div>
        </Row>
      )}
    </Fragment>
  );
};

export default TableContainer;
